// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { TransformedPost, getProxiedUrl } from './types';

interface Props {
  isOpen: boolean;
  username: string;
  posts: TransformedPost[];
  onClose: () => void;
  onImportSelected: (username: string, selectedPosts: TransformedPost[]) => void;
}

const ProxiedThumbnail: React.FC<{
  url: string;
  selected: boolean;
  type: string;
  onClick: () => void;
}> = ({ url, selected, type, onClick }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const proxied = await getProxiedUrl(url);
        if (!cancelled) setSrc(proxied);
      } catch {
        if (!cancelled) setSrc(url);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (url) load();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-300 scale-[0.97]' : 'border-transparent hover:border-gray-300'
      }`}
    >
      <div className="relative" style={{ aspectRatio: '1/1' }}>
        {loading ? (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-400 text-xs">Loading...</div>
          </div>
        ) : (
          <img
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+PC9zdmc+';
            }}
          />
        )}

        {/* Selection checkmark */}
        {selected && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}

        {/* Type indicator */}
        {type === 'Video' && (
          <div className="absolute bottom-2 left-2">
            <div className="bg-black/60 rounded-full p-1">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}
        {type === 'Sidecar' && (
          <div className="absolute bottom-2 left-2">
            <div className="bg-black/60 rounded-full p-1">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PostGalleryModal: React.FC<Props> = ({ isOpen, username, posts, onClose, onImportSelected }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset selection when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen, username]);

  if (!isOpen || !posts.length) return null;

  const togglePost = (postId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(posts.map(p => p.igPostId)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const handleImport = () => {
    const selected = posts.filter(p => selectedIds.has(p.igPostId));
    if (selected.length > 0) {
      onImportSelected(username, selected);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white">@{username}'s Posts</h3>
            <p className="text-sm text-white/80">{posts.length} posts collected · {selectedIds.size} selected</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={selectAll}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={selectNone}
              className="text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Deselect All
            </button>
          </div>
          <span className="text-xs text-gray-500">
            Click to select posts for import
          </span>
        </div>

        {/* Gallery Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {posts.map((post) => (
              <ProxiedThumbnail
                key={post.igPostId}
                url={post.displayUrl}
                selected={selectedIds.has(post.igPostId)}
                type={post.type}
                onClick={() => togglePost(post.igPostId)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selectedIds.size === 0}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
          >
            Import {selectedIds.size} Post{selectedIds.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostGalleryModal;
